import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  assignmentKey,
  assignmentSubtitle,
  clearStoredPortalRoleChoice,
  distinctSelectableAssignments,
  isOhtScopedRoleName,
  normRoleName,
  persistPortalAssignment,
} from "../../../utils/portalRoleChoice";

type BootstrapRole = {
  roleName: string;
  clinicId?: number | string | null;
  clinicName?: string | null;
  regionId?: number | null;
  ohtId?: number | null;
  ohtName?: string | null;
  ohtCode?: string | null;
  isActive?: boolean | number | null;
};

type BootstrapResponse = {
  userId: number;
  status: "Pending" | "Approved" | "Disabled" | string;
  roles?: BootstrapRole[];
  primaryRole?: string | null;
};

function isRoleActive(v: any): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  return String(v).toLowerCase() === "true";
}

function routeForRoleName(roleName: string): string | null {
  const r = normRoleName(roleName);
  if (!r) return null;

  // ✅ Locked v1: SuperAdmin has its own portal path (do NOT collapse into SystemAdmin)
  if (r === "superadmin") return "/portal/super-admin";
  if (r === "ohtadmin") return "/portal/oht-admin";
  if (r === "communitynavigator") return "/portal/community-navigator";

  if (r === "clinicadmin") return "/portal/clinic-admin";
  if (r === "familydoctor") return "/portal/family-doctor";
  if (r === "specialist") return "/portal/specialist";
  if (r === "regionadmin") return "/portal/region-admin";

  // Keep placeholder admin portal for Admin/SystemAdmin (if present)
  if (r === "admin" || r === "systemadmin") return "/portal/admin";

  return null;
}

function labelForRole(roleName: string): string {
  const r = normRoleName(roleName);
  if (r === "familydoctor") return "Family Doctor";
  if (r === "clinicadmin") return "Clinic Admin";
  if (r === "specialist") return "Specialist";
  if (r === "regionadmin") return "Region Admin";
  if (r === "ohtadmin") return "OHT Admin";
  if (r === "communitynavigator") return "Community Navigator";
  if (r === "superadmin") return "Super Admin";
  if (r === "admin" || r === "systemadmin") return "System Admin";
  return roleName;
}

function descForRole(roleName: string): string {
  const r = normRoleName(roleName);
  if (r === "familydoctor")
    return "Review new patient requests, manage your profile, and use provider tools.";
  if (r === "clinicadmin")
    return "Manage clinic access, users, and clinic-level configuration.";
  if (r === "specialist")
    return "Manage your specialist profile and referral-related tools.";
  if (r === "regionadmin")
    return "Region-wide admin tools, oversight, and reporting.";
  if (r === "ohtadmin")
    return "OHT analytics, clinic messaging, and regional operational visibility.";
  if (r === "communitynavigator")
    return "Manage navigator cases, complete intake, match patients to providers, and forward suitable cases.";
  if (r === "superadmin")
    return "Break-glass approvals and platform integrity tools.";
  if (r === "admin" || r === "systemadmin")
    return "System-wide administration and configuration.";
  return "Open portal tools for this role.";
}

function rolePill(roleName: string): {
  text: string;
  bg: string;
  border: string;
  color: string;
} {
  const r = normRoleName(roleName);
  if (r === "familydoctor")
    return { text: "Clinical", bg: "#eef2ff", border: "#c7d2fe", color: "#3730a3" };
  if (r === "clinicadmin")
    return { text: "Operations", bg: "#ecfeff", border: "#a5f3fc", color: "#155e75" };
  if (r === "specialist")
    return { text: "Provider", bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" };
  if (r === "regionadmin")
    return { text: "Regional", bg: "#fff7ed", border: "#fed7aa", color: "#7c2d12" };
  if (r === "ohtadmin")
    return { text: "OHT", bg: "#f5f3ff", border: "#ddd6fe", color: "#5b21b6" };
  if (r === "communitynavigator")
    return { text: "Navigator", bg: "#f0f9ff", border: "#bae6fd", color: "#075985" };
  if (r === "superadmin")
    return { text: "Break-glass", bg: "#ffecec", border: "#f5c2c2", color: "#7f1d1d" };
  return { text: "Admin", bg: "#f1f5f9", border: "#e2e8f0", color: "#0f172a" };
}

export default function SelectRolePage() {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [roles, setRoles] = useState<BootstrapRole[]>([]);
  const [error, setError] = useState<string | null>(null);

  const activeRoles = useMemo(
    () => distinctSelectableAssignments(roles, (role) => isRoleActive(role?.isActive)),
    [roles]
  );

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        const meRes = await fetch("/.auth/me", { credentials: "include" });
        const me = await meRes.json();

        if (!me?.clientPrincipal) {
          window.location.href = "/portal/login";
          return;
        }

        setEmail(me.clientPrincipal.userDetails || null);

        const bootRes = await fetch("/api/portal-auth-bootstrap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!bootRes.ok) {
          const txt = await bootRes.text();
          throw new Error(`Bootstrap failed (${bootRes.status}): ${txt}`);
        }

        const boot = (await bootRes.json()) as BootstrapResponse;

        if (boot.status === "Pending") {
          window.location.href = "/portal/access-pending";
          return;
        }
        if (boot.status === "Disabled") {
          window.location.href = "/portal/access-disabled";
          return;
        }

        const rs = Array.isArray(boot.roles) ? boot.roles : [];
        setRoles(rs);
        setLoading(false);

        // One selectable assignment does not need a choice.
        // OHT-scoped rows without an OHT are not selectable.
        const selectable = distinctSelectableAssignments(rs, (role) =>
          isRoleActive(role?.isActive)
        );
        if (selectable.length === 1) {
          const only = selectable[0];
          const route = routeForRoleName(only?.roleName || "");
          if (route && persistPortalAssignment(only)) {
            window.location.href = route;
          }
        }
      } catch (e: any) {
        setError(e?.message || "Failed to load roles.");
        setLoading(false);
      }
    };

    run();
  }, []);

  const onChoose = (role: BootstrapRole) => {
    const roleKey = normRoleName(role?.roleName);
    const route = routeForRoleName(roleKey);

    if (!route) {
      setError("Unknown role. Please contact support.");
      return;
    }

    if (!persistPortalAssignment(role)) {
      setError(
        roleKey === "clinicadmin"
          ? "This Clinic Admin assignment is missing a clinic. Please contact support."
          : isOhtScopedRoleName(roleKey)
          ? "This assignment is missing an Ontario Health Team. Please contact support."
          : "Unable to save role choice. Please contact support."
      );
      return;
    }

    window.location.href = route;
  };

  const onClearChoice = () => {
    clearStoredPortalRoleChoice();
    // Refresh so the role list re-renders cleanly
    window.location.href = "/portal/select-role";
  };

  const signOutHref = useMemo(() => {
    const postLogout = encodeURIComponent(
      `${window.location.origin}/portal/login`
    );
    return `/.auth/logout?post_logout_redirect_uri=${postLogout}`;
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily:
          "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "100%", maxWidth: 900 }}>
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <Link
            to="/public"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
              color: "#0f172a",
              fontWeight: 900,
              letterSpacing: "-0.02em",
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
              }}
              aria-hidden
            >
              <img src="/logo-1.png" alt="" style={{ width: 28, height: 28 }} />
            </span>
            AcceptingMD
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <a
              href={signOutHref}
              style={{
                textDecoration: "none",
                color: "#334155",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              Sign out
            </a>
          </div>
        </div>

        {/* “Modal-like” card */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 18,
            padding: 20,
            boxShadow: "0 18px 46px rgba(15, 23, 42, 0.10)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 950,
                  color: "#0f172a",
                }}
              >
                Select your role
              </h1>
              <p
                style={{
                  marginTop: 6,
                  marginBottom: 0,
                  color: "#475569",
                  lineHeight: 1.5,
                }}
              >
                Choose how you want to use the portal right now.
                {email ? ` (${email})` : ""}
              </p>
            </div>

            <button
              type="button"
              onClick={onClearChoice}
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
              title="Clear saved role choice"
            >
              Clear saved choice
            </button>
          </div>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 12,
                background: "#ffecec",
                border: "1px solid #f5c2c2",
                color: "#7f1d1d",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 950, marginBottom: 6 }}>Error</div>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ marginTop: 14, color: "#64748b", fontSize: 13 }}>
              Loading roles…
            </div>
          ) : (
            <>
              {activeRoles.length === 0 ? (
                <div style={{ marginTop: 14, color: "#64748b" }}>
                  No active roles assigned.{" "}
                  <Link
                    to="/portal/no-access"
                    style={{ color: "#0017a2", fontWeight: 900 }}
                  >
                    View details
                  </Link>
                  .
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 16,
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 12,
                  }}
                >
                  {activeRoles.map((r) => {
                    const key = assignmentKey(r) || normRoleName(r.roleName);
                    const roleKey = normRoleName(r.roleName);
                    const route = routeForRoleName(roleKey);
                    const pill = rolePill(roleKey);
                    const subtitle = assignmentSubtitle(r);
                    const showClinicIdFooter =
                      roleKey !== "clinicadmin" && !!r.clinicId;
                    const showOhtFooter = !isOhtScopedRoleName(roleKey) && !!r.ohtId;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onChoose(r)}
                        style={{
                          textAlign: "left",
                          padding: 14,
                          borderRadius: 16,
                          border: "1px solid #e2e8f0",
                          background: "#ffffff",
                          cursor: route ? "pointer" : "not-allowed",
                          boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
                        }}
                        disabled={!route}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 950,
                              color: "#0f172a",
                            }}
                          >
                            {labelForRole(roleKey)}
                          </div>

                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 900,
                              padding: "4px 8px",
                              borderRadius: 999,
                              background: pill.bg,
                              border: `1px solid ${pill.border}`,
                              color: pill.color,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {pill.text}
                          </span>
                        </div>

                        {subtitle ? (
                          <div
                            style={{
                              marginTop: 6,
                              color: "#0f172a",
                              fontSize: 13,
                              fontWeight: 800,
                              lineHeight: 1.4,
                            }}
                          >
                            {subtitle}
                          </div>
                        ) : null}

                        <div
                          style={{
                            marginTop: 8,
                            color: "#475569",
                            fontSize: 13,
                            lineHeight: 1.5,
                          }}
                        >
                          {descForRole(roleKey)}
                        </div>

                        {(showClinicIdFooter || r.regionId || showOhtFooter) && (
                          <div
                            style={{
                              marginTop: 10,
                              color: "#64748b",
                              fontSize: 12,
                            }}
                          >
                            {showClinicIdFooter
                              ? String(r.clinicName || "").trim() ||
                                `Clinic #${r.clinicId}`
                              : ""}
                            {showClinicIdFooter && (r.regionId || showOhtFooter)
                              ? " • "
                              : ""}
                            {r.regionId ? `Region #${r.regionId}` : ""}
                            {r.regionId && showOhtFooter ? " • " : ""}
                            {showOhtFooter
                              ? r.ohtName
                                ? `OHT: ${r.ohtName}`
                                : r.ohtCode
                                ? `OHT: ${r.ohtCode}`
                                : `OHT #${r.ohtId}`
                              : ""}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ color: "#64748b", fontSize: 12 }}>
                  Tip: You can switch roles anytime by returning to{" "}
                  <span style={{ fontWeight: 900, color: "#0f172a" }}>
                    /portal/select-role
                  </span>
                  .
                </div>

                <Link
                  to="/portal"
                  style={{
                    textDecoration: "none",
                    color: "#0017a2",
                    fontSize: 13,
                    fontWeight: 900,
                  }}
                >
                  Back to Portal
                </Link>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: 18,
            fontSize: 12,
            color: "#64748b",
          }}
        >
          © {new Date().getFullYear()} AcceptingMD
        </div>
      </div>
    </div>
  );
}